import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useJourneyUpdatesSse,
  JourneyOnCallEvent,
  JourneySseEvent,
} from "@/modules/leads/hooks/use-journey-updates-sse.hook";

interface Params {
  orgUid?: string;
  journeyUid?: string;
}

// Subscribes the single lead-detail page (desktop or mobile — both share this host) to live
// on-call updates for the one journey being viewed, and patches the ["getLead"] cache in place.
export function useLeadPageOnCallSse({ orgUid, journeyUid }: Params): void {
  const queryClient = useQueryClient();

  const onSseEvent = useCallback(
    (event: JourneySseEvent) => {
      if (event.type !== "oncall") return;
      const { journeyUid: eventJourneyUid, isOnCall } =
        event.payload as JourneyOnCallEvent["payload"];
      // Raw ["getLead"] cache shape: { data: { isOnCall, lead: { journey_uid, ... }, ... } }.
      queryClient.setQueriesData<{ data?: { isOnCall?: boolean; lead?: { journey_uid?: string } } }>(
        { queryKey: ["getLead"] },
        (cached) => {
          if (!cached?.data) return cached;
          // Guard: only patch when the event targets the journey this page is showing.
          if (cached.data.lead?.journey_uid && cached.data.lead.journey_uid !== eventJourneyUid) {
            return cached;
          }
          return { ...cached, data: { ...cached.data, isOnCall } };
        },
      );
    },
    [queryClient],
  );

  useJourneyUpdatesSse({
    orgUid,
    visibleJourneyUids: journeyUid ? [journeyUid] : [],
    onEvent: onSseEvent,
    enabled: Boolean(orgUid && journeyUid),
  });
}
